// AuthPanel.tsx
import { AuthModalTab } from "@/context/Auth";
import { css } from "@emotion/react";
import styled from "@emotion/styled";
import { User, UserCredential, getAuth } from "firebase/auth";
import React, { JSX, useState } from "react";
import { useRouter } from "next/navigation";
import LoginForm from "./LoginForm";
import OTPform from "./OTPForm";
import ResetPassForm from "./ResetPassForm";
import SignupForm from "./SignupForm";
import { useAuthContext } from "@/context/Auth";
import PrivacyPolicyPopup from "../profile/PrivacyPolicyPopup";
import profileService from "@/services/profileService";
import { generateDeviceFingerprint, getDeviceInfo } from "@/utils/deviceFingerprint";

const Root = styled("div", {
  shouldForwardProp: (prop: PropertyKey) => ![""].includes(prop.toString()),
})<Partial<AuthPanelCombinedProps>>((props) => css``);

export interface AuthPanelProps {
  defaultTab?: AuthModalTab;
  onCloseRequest?: () => void;
}

export type AuthPanelCombinedProps = AuthPanelProps &
  JSX.IntrinsicElements["div"];

const AuthPanel: React.FC<AuthPanelCombinedProps> = ({
  defaultTab = "login",
  onCloseRequest,
  ...props
}) => {
  const router = useRouter();
  const auth = getAuth();
  const [activeTab, setActiveTab] = React.useState<AuthModalTab>(defaultTab);
  const [user, setUser] = useState<User | null>(auth.currentUser);
  const [trustDevice, setTrustDevice] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [isAuthProcessing, setIsAuthProcessing] = useState<boolean>(false);
  const { dispatch } = useAuthContext();
  const [privacyPolicyOpened, setPrivacyPolicyOpened] = useState(false);

  // Sync activeTab with defaultTab prop changes
  // Only sync when defaultTab changes, not when activeTab changes locally
  React.useEffect(() => {
    console.log(`🔄 DefaultTab changed to: ${defaultTab}`);
    setActiveTab(defaultTab);
  }, [defaultTab]);

  // Listen for auth state changes to keep user state in sync
  React.useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged((currentUser) => {
      setUser(currentUser);
    });

    return () => unsubscribe();
  }, [auth]);

  const isPrivacyPolicyAccepted = () => {
    return localStorage.getItem('privacy_policy_accepted') === 'true';
  };

  const handleSuccessfulAuth = React.useCallback(async (userEmail?: string) => {
    try {
      onCloseRequest?.();

      // Get user email
      const email = userEmail || user?.email || getAuth().currentUser?.email;
      if (!email) {
        console.error('No user email available for navigation');
        await router.push('/profile');
        return;
      }

      // Fetch user profile from database to check onboarding status
      let redirectPath = '/profile'; // Default to profile

      try {
        const profile = await profileService.fetchUserProfileByEmail(email);

        if (profile) {
          // Check if onboarding is complete (both privacy policy and terms accepted)
          const onboardingComplete = profile.privacyPolicyAccepted && profile.termsOfServiceAccepted;

          if (!onboardingComplete) {
            redirectPath = '/onboarding';
            console.log('🚀 Redirecting to onboarding (not completed)');
          } else {
            console.log('✅ Onboarding complete, redirecting to profile');
          }
        } else {
          // If profile doesn't exist yet, send to onboarding
          redirectPath = '/onboarding';
          console.log('🚀 No profile found, redirecting to onboarding');
        }
      } catch (profileError) {
        console.error('Error fetching profile:', profileError);
        // On error fetching profile, send to onboarding to be safe
        redirectPath = '/onboarding';
      }

      // Navigate to determined path
      console.log(`📍 Navigating to: ${redirectPath}`);
      await router.push(redirectPath);

      // Fallback navigation after a short delay
      setTimeout(() => {
        if (window.location.pathname !== redirectPath) {
          console.log(`🔄 Fallback navigation to: ${redirectPath}`);
          window.location.href = redirectPath;
        }
      }, 100);

    } catch (error) {
      console.error('Navigation error:', error);
      // Fallback to onboarding on error to be safe
      window.location.href = "/onboarding";
    }
  }, [onCloseRequest, router, user]);

  const handleAuth = React.useCallback(async (creds: UserCredential) => {
    if (isAuthProcessing) {
      return;
    }

    try {
      setIsAuthProcessing(true);
      setError(null);

      // Get user token
      const token = await creds.user.getIdToken();

      const isGoogleUser = creds.user.providerData.some(
        provider => provider.providerId === 'google.com'
      );

      // First verify session to get user role
      const verifyResponse = await fetch('/api/auth/verify-session', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!verifyResponse.ok) {
        throw new Error('Failed to verify session');
      }

      const verifyData = await verifyResponse.json();
      const userRole = "admin";//verifyData.userRole;

      // Store privacy policy acceptance in database if it was accepted
      if (isPrivacyPolicyAccepted()) {
        try {
          await fetch('/api/user/privacy-policy', {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            },
            body: JSON.stringify({
              accepted: true,
              acceptedAt: new Date().toISOString()
            })
          });
        } catch (error) {
          console.error('Failed to store privacy policy acceptance:', error);
          // Don't throw here, as we still want to proceed with auth
        }
      }

      // For Google users, skip OTP entirely
      if (isGoogleUser) {
        console.log('✅ Google user - skipping OTP verification');

        // Mark as authenticated to prevent Auth context from running session check
        dispatch({
          type: '2FA_ACCEPT',
          payload: {
            isTrustedDevice: true,
            isVerified: true
          }
        });

        await handleSuccessfulAuth(creds.user.email || undefined);
        return;
      }

      // Create OTP for non-Google users
      // Generate device fingerprint for trusted device tracking
      const deviceFingerprint = generateDeviceFingerprint();
      const deviceInfo = getDeviceInfo();

      console.log('🔐 Generated device fingerprint:', deviceFingerprint);
      console.log('🔐 Device info:', deviceInfo);
      console.log('🔐 Trust device:', trustDevice);

      const response = await fetch("/api/auth/create-otp", {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          trustDevice,
          deviceFingerprint,
          deviceInfo
        })
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || 'Failed to setup verification');
      }

      const data = await response.json();
      console.log('🔐 OTP API response:', data);

      // Check if OTP is required or device is trusted
      if (data.requiresOTP === false) {
        console.log('✅ Trusted device detected by server, skipping OTP verification');

        // Mark as authenticated to prevent Auth context from running session check
        dispatch({
          type: '2FA_ACCEPT',
          payload: {
            isTrustedDevice: true,
            isVerified: true
          }
        });

        // Device is trusted (verified by server via device fingerprint), proceed with auth
        await handleSuccessfulAuth(creds.user.email || undefined);
        return;
      }

      // OTP is required - show verification modal
      console.log('📧 OTP required, showing verification modal');

      setUser(creds.user);

      // Update Auth context to show verify-otp modal
      dispatch({
        type: 'OPEN_MODAL',
        payload: {
          defaultTab: 'verify-otp',
          isFirstLogin: false // Always treat as returning user since server tracks this
        }
      });

    } catch (error) {
      console.error('Error during authentication:', error);
      setError(error instanceof Error ? error.message : 'Authentication failed');
    } finally {
      setIsAuthProcessing(false);
    }
  }, [isAuthProcessing, trustDevice, handleSuccessfulAuth, isPrivacyPolicyAccepted]);

  const handleSignupRequest = () => {
    setActiveTab("signup");
  };

  const handlePrivacyPolicyAccept = () => {
    dispatch({ type: 'PRIVACY_POLICY_ACCEPTED' });
    setPrivacyPolicyOpened(false);
    setActiveTab("signup");
  };

  return (
    <Root {...props} style={{ overflow: "auto", height: "100%" }}>
      {error && (
        <div className="text-red-500 text-sm mb-4 p-2 bg-red-50 rounded">
          {error}
        </div>
      )}
      {
        {
          login: (
            <LoginForm
              onLogin={handleAuth}
              onSignupRequest={handleSignupRequest}
              onPasswordResetRequest={() => setActiveTab("resetpass")}
              trustDevice={trustDevice}
              onTrustDeviceChange={setTrustDevice}
            />
          ),
          signup: (
            <SignupForm
              onLoginRequest={() => setActiveTab("login")}
              onCloseRequest={onCloseRequest}
            />
          ),
          resetpass: (
            <ResetPassForm
              onLoginRequest={() => setActiveTab("login")}
              onCloseRequest={onCloseRequest}
            />
          ),
          "verify-otp": (
            <OTPform
              user={user}
              trustDevice={trustDevice}
              onClose={async (verified) => {
                if (verified && user) {
                  try {
                    const token = await user.getIdToken();
                    const verifyResponse = await fetch('/api/auth/verify-session', {
                      method: 'POST',
                      headers: {
                        'Authorization': `Bearer ${token}`,
                        'Content-Type': 'application/json'
                      }
                    });
                    if (!verifyResponse.ok) {
                      throw new Error('Failed to verify session');
                    }
                    await verifyResponse.json();
                    await handleSuccessfulAuth(user.email || undefined);
                  } catch (error) {
                    console.error('Error during OTP verification:', error);
                    setError(error instanceof Error ? error.message : 'Verification failed');
                  }
                }
              }}
            />
          ),
        }[activeTab]
      }

      <PrivacyPolicyPopup
        opened={privacyPolicyOpened}
        onClose={() => setPrivacyPolicyOpened(false)}
        onAccept={handlePrivacyPolicyAccept}
      />
    </Root>
  );
};

export default AuthPanel;